import React, { useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TablePagination,
} from '@mui/material';
import LoadingSpinner from '../../components/LoadingSpinner';
import SmartFilterPanel from './components/SmartFilterPanel';
import ProfitTableRow from './components/ProfitTableRow';
import ProfitTableFooter from './components/ProfitTableFooter';
import ProfitTableHeader from './components/ProfitTableHeader';
import UserDetailsPanel from './components/UserDetailsPanel';
import { useWinLoseReport } from '../../hooks/useWinLoseReport';
import { calculateTotals, exportReport } from '../../utils/reportUtils';
import reportService from '../../services/reportService';

const getViewLabel = (level) => {
    if (level === 0) {
        return 'Senior Master';
    }

    if (level === 1) {
        return 'Master';
    }

    return 'Agent';
};

const ProfitTable = () => {
    const { showData, loading, startDate, endDate, setShowData, setStartDate, setEndDate, fetchReportData } = useWinLoseReport();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [alignment, setAlignment] = useState("left");
    const [fontsize, setFontsize] = useState("text-sm");
    const [selectedValue, setSelectedValue] = useState('default');
    const [checked, setChecked] = useState({ single: true, mix: true, total: true });
    const [reportPath, setReportPath] = useState([]);
    const [selectedUserDetails, setSelectedUserDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const currentRows = showData;
    const visibleRows = currentRows;
    const currentView = reportPath[reportPath.length - 1] || null;
    const profitColumnTitle = currentView ? `${getViewLabel(reportPath.length)} Profit` : 'Senior Master Profit';
    const pageRows = useMemo(
        () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [visibleRows, page, rowsPerPage]
    );
    const totals = calculateTotals(visibleRows, checked);

    const handleChange = async (event, type) => {
        setChecked(prev => ({
            ...prev,
            [type]: event.target.checked
        }));
    };

    const handleAlignment = async (event, newAlignment) => {
        if (newAlignment) {
            setAlignment(newAlignment);
        }
    };

    const handleAmountSortChange = (type) => {
        const sortedRows = [...visibleRows];

        if (type === 'asc') {
            sortedRows.sort((a, b) => a.amount - b.amount);
        }
        else if (type === 'desc') {
            sortedRows.sort((a, b) => b.amount - a.amount);
        }
        else {
            sortedRows.sort((a, b) => a.name.localeCompare(b.name));
        }

        setShowData(sortedRows);
        setPage(0);
        setSelectedValue(type);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const loadUserReport = async (userId) => {
        await fetchReportData({ startDate, endDate, userId });
    };

    const handleUserIdClick = async (row) => {
        if (Number(row.subRoleId) === 5) {
            const selectedId = row.subUserId || row.id || row.userId;

            setDetailsLoading(true);
            setSelectedUserDetails({
                id: selectedId,
                userId: row.userId,
                details: [],
            });
            setPage(0);
            setSelectedValue('default');

            try {
                const response = await reportService.winloseReportDetails({ startDate, endDate, userId: selectedId });

                setSelectedUserDetails({
                    id: selectedId,
                    userId: row.userId,
                    details: Array.isArray(response.data) ? response.data : response.data?.data || [],
                });
            } catch (error) {
                console.error('Failed to fetch user details:', error);
                setSelectedUserDetails({
                    id: selectedId,
                    userId: row.userId,
                    details: [],
                    error: 'Unable to load user details.',
                });
            } finally {
                setDetailsLoading(false);
            }

            return;
        }

        const nextView = {
            id: row.id || row.userId,
            userId: row.userId,
            name: row.name,
            level: reportPath.length,
        };

        setReportPath(prev => [...prev, nextView]);
        setSelectedUserDetails(null);
        setPage(0);
        setSelectedValue('default');
        await loadUserReport(nextView.id);
    };

    const handleBackToReport = async () => {
        const nextPath = reportPath.slice(0, -1);
        const parentView = nextPath[nextPath.length - 1];

        setReportPath(nextPath);
        setSelectedUserDetails(null);
        setPage(0);
        setSelectedValue('default');
        await loadUserReport(parentView?.id || 1);
    };

    const handleSearch = async () => {
        setReportPath([]);
        setSelectedUserDetails(null);
        setPage(0);
        setSelectedValue('default');
        await loadUserReport(1);
    };

    return (
        <div className='px-2 bg-customLightMilkWhite dark:bg-custom_dark_background min-h-screen'>
            {loading ? <LoadingSpinner /> : null}
            <div className='flex flex-wrap items-center gap-2 my-1'>
                <p className='text-base font-bold dark:text-customWhite'>Smart Win Lose Report</p>
                {reportPath.length ? (
                    <div className='flex flex-wrap items-center gap-1 text-xs font-semibold text-background dark:text-customWhite'>
                        {reportPath.map((view, index) => (
                            <React.Fragment key={`${view.level}-${view.id}-${index}`}>
                                <span>/</span>
                                <button
                                    type='button'
                                    className={`${index === reportPath.length - 1 ? 'text-customOrange dark:text-customLightYellow' : 'hover:underline'}`}
                                    onClick={async () => {
                                        const nextPath = reportPath.slice(0, index + 1);

                                        setReportPath(nextPath);
                                        setSelectedUserDetails(null);
                                        setPage(0);
                                        setSelectedValue('default');
                                        await loadUserReport(view.id);
                                    }}
                                >
                                    {`${getViewLabel(index)} - ${view.userId}`}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                ) : null}
            </div>
            <SmartFilterPanel
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleSearch={handleSearch}
                handleExport={(type) => exportReport(visibleRows, startDate, endDate, type)}
                fontsize={fontsize}
                setFontsize={setFontsize}
                checked={checked}
                setChecked={setChecked}
                alignment={alignment}
                setAlignment={setAlignment}
                handleChange={handleChange}
                handleAlignment={handleAlignment}
                showBackButton={Boolean(currentView)}
                handleBack={handleBackToReport}
                backLabel="Back"
            />

            <TableContainer component={Paper} className="shadow-md rounded-xl overflow-x-auto">
                <Table aria-label="smart win lose report table" size="small">
                    <ProfitTableHeader
                        amountSort={selectedValue}
                        onAmountSortChange={handleAmountSortChange}
                        profitColumnTitle={profitColumnTitle}
                    />
                    <TableBody>
                        {pageRows.map((row, idx) => (
                            <React.Fragment key={`${row.userId}-${page * rowsPerPage + idx}`}>
                                <ProfitTableRow
                                    row={row}
                                    idx={page * rowsPerPage + idx}
                                    checked={checked}
                                    alignment={alignment}
                                    fontsize={fontsize}
                                    onUserIdClick={handleUserIdClick}
                                />
                                {String(selectedUserDetails?.id) === String(row.id || row.userId) ? (
                                    <TableRow className="dark:bg-custom_dark_background">
                                        <TableCell colSpan={14} sx={{ border: '1px solid #aaa', padding: '12px' }}>
                                            <UserDetailsPanel
                                                user={selectedUserDetails}
                                                loading={detailsLoading}
                                                onClose={() => setSelectedUserDetails(null)}
                                                startDate={startDate}
                                                endDate={endDate}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </React.Fragment>
                        ))}
                    </TableBody>
                    <ProfitTableFooter
                        totals={totals}
                        alignment={alignment}
                        fontsize={fontsize}
                    />
                </Table>
                <TablePagination
                    component="div"
                    count={visibleRows.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 15, 20, 50]}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    className="bg-customLightMilkWhite dark:bg-custom_dark_primary2 dark:text-customWhite"
                />
            </TableContainer>

        </div>
    );
};

export default ProfitTable;

